import { Injectable } from '@angular/core';
import { SessionService } from '@edl-development/edl-common';
import { RawParams, StateService } from '@uirouter/core';
import { ObservationTypeValues } from '../model/enum/observation-type-values.enum';
import { ObservationTypes } from '../model/enum/observation-types.enum';
import { DuplicateObservationDTO } from '../model/observation/common/duplicate-observation-dto.model';
import { UpdateEvaluatorRequest } from '../model/observation/common/update-evaluator-request.model';
import { ObservationSummaryDTO } from '../model/observation/observation-summary-dto.model';
import { ObservationListApi } from './../api/observation-list.api';

@Injectable()
export class ObservationListService {

    constructor(
        public stateService: StateService,
        private observationListApi: ObservationListApi,
        private sessionService: SessionService
    ) { }

    openObservation = (userId: string, observation?: ObservationSummaryDTO, detailsOverride: boolean = false, destinationTabIndex?: number, extraParams: { [paramName: string]: any } = {}) => {
        var destinationTab = destinationTabIndex ?? this.generateDestinationTab(observation?.itemType, observation?.oStatus, userId);
        const state = this.generateState(userId, observation?.observationId, observation?.itemType, detailsOverride, destinationTab, extraParams);
        this.stateService.go(state.to, state.params);
    }

    getObservationLink = (userId: string, observationId: string, itemType: ObservationTypeValues, detailsOverride: boolean = false, destinationTabIndex?: number, extraParams: { [paramName: string]: any } = {}) => {
        const state = this.generateState(userId, observationId, itemType, detailsOverride, destinationTabIndex, extraParams);
        return this.stateService.href(state.to, state.params);
    }

    private generateState(userId: string, observationId?: string, itemType?: ObservationTypeValues, detailsOverride: boolean = false, destinationTabIndex?: number, extraParams: { [paramName: string]: any } = {}): { to: string, params: RawParams } {
        let observationType = ObservationTypes.List.find(x => x.Value == itemType);

        if (detailsOverride || !observationType?.UseDashboard) {
            return {
                to: 'authenticated.user-summary.' + observationType?.URLTag + '.details',
                params: {
                    observationId,
                    userId: userId,
                    tabIndex: destinationTabIndex,
                    ...extraParams
                }
            };
        } else {
            return {
                to: 'authenticated.user-summary.' + observationType.URLTag + '.dashboard.view',
                params: {
                    observationId,
                    userId: userId,
                    ...extraParams
                }
            }
        }
    }

    openPrintPreview = (observationType: ObservationTypeValues, observationId: string, targetUserId: string, extraParams: { [paramName: string]: any } = {}) => {
        return this.openPrintPreview_internal(targetUserId, observationId, observationType, extraParams);
    }


    private openPrintPreview_internal = (userId: string, observationId: string, itemType: ObservationTypeValues, extraParams: { [paramName: string]: any } = {}) => {
        const state = this.generatePrintState(userId, observationId, itemType, extraParams);
        const href = this.stateService.href(state.to, state.params);
        window.open(href, '_blank');
    }

    private generatePrintState(userId: string, observationId?: string, itemType?: ObservationTypeValues, extraParams: { [paramName: string]: any } = {}): { to: string, params: RawParams } {
        const observationUrlTag = ObservationTypes.List.find(x => x.Value == itemType)?.URLTag;
        return {
            to: `authenticated.print.observation.${observationUrlTag}`,
            params: {
                userId,
                observationId,
                observationUrlTag: observationUrlTag,
                ...extraParams
            }
        }
    }


    private generateDestinationTab(itemType?: number, ostatus?: string, userId?: string) {
        if (!itemType || !ostatus) return undefined;
        let isEmployee = userId === this.sessionService?.user?.UserID;

        switch (itemType) {
            case ObservationTypes.SLO.Type:
                return ["In Draft", "Saved by Employee"].includes(ostatus) && isEmployee ? 1 : 6;
            default:
                // return ObservationTypes.List.find(x => x.Value == itemType)?.DefaultTab;
                return undefined;
        }
    }

    navigateOnCreation(targetUserId?: string, observationSummary?: ObservationSummaryDTO, destinationTabIndex?: number, extraParams: { [paramName: string]: any } = {}) {
        let currentUserId = this.sessionService?.user?.UserID;
        if ((currentUserId === observationSummary?.waitingOnId ||
            currentUserId === observationSummary?.evalUser?.userID)
            && targetUserId && observationSummary?.permissions?.canView) {
            this.openObservation(targetUserId, observationSummary, false, destinationTabIndex, extraParams);
        } else {
            this.stateService.go('.', {}, { reload: true });
        }
    }

    getUserSummary = (userId: string, observationType: number, schoolYear: string) =>
        this.observationListApi.getUserSummary(userId, observationType, schoolYear);

    getObservationSummary = (userId: string, observationType: number, observationId: string) =>
        this.observationListApi.getObservationSummary(userId, observationType, observationId);

    duplicateObservation = (duplicateObservationDTO: DuplicateObservationDTO) =>
        this.observationListApi.duplicateObservation(duplicateObservationDTO);

    reopenObservation = (userId: string, observationType: number, observationId: string) =>
        this.observationListApi.reopenObservation(userId, observationType, observationId);

    deleteObservation = (userId: string, observationType: number, observationId: string) =>
        this.observationListApi.deleteObservation(userId, observationType, observationId);

    reinstateObservation = (userId: string, observationType: number, observationId: string) =>
        this.observationListApi.reinstateObservation(userId, observationType, observationId);

    updateSchoolYear = (userId: string, observationType: number, observationId: string, schoolYear: string) =>
        this.observationListApi.updateSchoolYear(userId, observationType, observationId, schoolYear);

    updateEvaluator = (request: UpdateEvaluatorRequest) =>
        this.observationListApi.updateEvaluator(request);

    getUserSummaryPermissions = (userId: string) =>
        this.observationListApi.getUserSummaryPermissions(userId);
}
